import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DocumentApi } from '../services/api/DocumentApi';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import CourseManageTab from '../components/courses/CourseManageTab';
import { Calendar as CalendarComponent } from "../components";
import { UnifiedActivities } from '../components/activities';
import Tabs from '../components/primitives/Tabs/Tabs';
import AIChatCard from "../components/aiChat/AiChatCard";
import { MyDocumentsCard, FileUpload, CourseDetailsSection } from '../components';
import TutorialVideoButton from '../components/common/TutorialVideoButton';
import "./CourseView.css";

// Inline state components (formerly in CourseStates.js)
const LoadingState = ({ message = "Loading..." }) => (
  <div className="course-view-container animate-fade-in-up">
    <div className="loading-state">
      <div className="loading-spinner"></div>
      <p>{message}</p>
    </div>
  </div>
);
const ErrorState = ({ error, onGoBack }) => (
  <div className="course-view-container animate-fade-in-up">
    <div className="error-state">
      <div className="error-icon">⚠️</div>
      <h2>Error Loading Course</h2>
      <p>{error}</p>
      <button className="btn btn-primary" onClick={onGoBack}>Go Back</button>
    </div>
  </div>
);
const NotFoundState = ({ onGoBack }) => (
  <div className="course-view-container animate-fade-in-up">
    <div className="error-state">
      <div className="error-icon">❓</div>
      <h2>Course Not Found</h2>
      <p>The course you're looking for doesn't exist.</p>
      <button className="btn btn-primary" onClick={onGoBack}>Go Back</button>
    </div>
  </div>
);

// ADD helper below imports
const getMetadataValue = (course, name) => {
  if (!course?.metadata) return null;
  const entry = course.metadata.find(m => m.metadataName?.toLowerCase() === name.toLowerCase());
  return entry?.value || null;
};

function CourseView() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  // Top focus sentinel to keep initial focus at top (prevent auto scroll to chat input)
  const topRef = useRef(null);
  
  const [course, setCourse] = useState(null);
  const [events, setEvents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syllabusDocId, setSyllabusDocId] = useState(null);
  const [syllabusDocName, setSyllabusDocName] = useState(null);
  const [docsRefreshKey, setDocsRefreshKey] = useState(0);
  // Reference for upcoming section wrapper
  // (kept for potential future use)

  /**
   * Detect syllabus document for this course (endeavor) – must be declared
   * BEFORE any early returns and BEFORE fetchCourseData (since it's a dependency).
   */
  // Access authenticated user via provided hook (avoids dynamic require + undefined context errors)
  const { user, hasAnyOrgAdminRole } = useAuth();
  const { getEndeavor, getEndeavorEvents, getEndeavorAssignments, isCourseOwnerOrProfessor, isSharedCourse: checkSharedCourse, loadProgressForEndeavor } = useData();

  const isShared = checkSharedCourse(Number(courseId));
  const isManager = isCourseOwnerOrProfessor(Number(courseId));
  const detectSyllabusDoc = useCallback(async (endeavorId) => {
    if (!user) {
      console.warn('CourseView: No user in context; skipping syllabus detection');
      setSyllabusDocId(null);
      return;
    }
    try {
      // Attempt to directly filter by a known syllabus documentTypeId if available.
      // If you have a constant for syllabus type id, replace  with that value.
      // Fallback: Use name search heuristic when type id not known.
      const SYLLABUS_TYPE_ID = undefined; // TODO: replace with real id if known (e.g., 3)
      const audioTypeIds = await (async () => {
        try {
          const { getAudioDocumentTypeIds } = await import('../utils/documentTypeUtils');
          return await getAudioDocumentTypeIds();
        } catch (e) {
          console.warn('Failed to get audio type IDs:', e);
          return [];
        }
      })();
      
      const params = {
        page: 0,
        size: 1, // only need the first match
        sort: 'createdAt,desc',
        endeavorId: endeavorId,
        excludeDocumentTypeIds: audioTypeIds
      };
      if (SYLLABUS_TYPE_ID) {
        params.documentTypeId = SYLLABUS_TYPE_ID;
      } else {
        // Use name filter if backend supports partial match on 'name'
        params.name = 'syllabus';
      }
      const resp = await DocumentApi.getDocumentsByUser(user.userId || user.id || user.user_id, params);
      if (!resp.ok) {
        console.warn('CourseView: syllabus detection request failed', resp.status);
        setSyllabusDocId(null);
        return;
      }
      const data = await resp.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data.content) ? data.content : (data.documents || []));
      let syllabusDoc = list[0];
      if (!SYLLABUS_TYPE_ID && syllabusDoc) {
        // Double-check it actually appears to be a syllabus in case name filter was broad
        const nm = (syllabusDoc.name || syllabusDoc.documentTypeName || syllabusDoc.typeName || '').toLowerCase();
        if (!nm.includes('syllabus')) {
          syllabusDoc = null;
        }
      }
      setSyllabusDocId(syllabusDoc ? (syllabusDoc.documentId || syllabusDoc.id) : null);
      setSyllabusDocName(syllabusDoc ? (syllabusDoc.name || 'Syllabus') : null);
    } catch (e) {
      console.warn('CourseView: Unable to detect syllabus document (user filter path)', e);
      setSyllabusDocId(null);
      setSyllabusDocName(null);
    }
  }, [user]);

  // Handle upload completion (refresh docs + re-detect syllabus)
  const handleUploadComplete = useCallback(() => {
    setDocsRefreshKey(k => k + 1);
    detectSyllabusDoc(courseId);
  }, [detectSyllabusDoc, courseId]);

  // Fetch all core course data (depends on detectSyllabusDoc)
  const fetchCourseData = useCallback(async (signal) => {
    try {
      setLoading(true);
      setError(null);

      // ✅ Use DataContext which checks cache first (from /full endpoint)
      console.log('CourseView - Fetching course data via DataContext');
      const courseData = await getEndeavor(courseId);
      
      // Check if request was cancelled
      if (signal?.aborted) {
        console.log('CourseView - Fetch cancelled for course:', courseId);
        return;
      }
      
      console.log('CourseView - Course data:', courseData);

      const augmentedCourse = {
        ...courseData,
        // Normalize metadata dates onto root for UI
        startDate: courseData.startDate || getMetadataValue(courseData, 'Start Date'),
        endDate: courseData.endDate || getMetadataValue(courseData, 'End Date')
      };
      setCourse(augmentedCourse);

      // ✅ Extract events from course data if available (from /full endpoint), otherwise fetch from cache
      if (courseData.events && Array.isArray(courseData.events) && courseData.events.length > 0) {
        console.log('CourseView - Using events from course data:', courseData.events);
        if (!signal?.aborted) setEvents(courseData.events);
      } else {
        // Fetch events using DataContext (checks cache first from /full hydration)
        console.log('CourseView - Fetching events via DataContext');
        const eventsArray = await getEndeavorEvents(courseId);
        if (signal?.aborted) return;
        console.log('CourseView - Fetched events:', eventsArray);
        setEvents(Array.isArray(eventsArray) ? eventsArray : []);
      }

      // ✅ Fetch assignments using DataContext (checks cache first from /full hydration)
      try {
        console.log('CourseView - Fetching assignments via DataContext');
        const assignmentsArray = await getEndeavorAssignments(courseId);
        if (signal?.aborted) return;
        console.log('CourseView - Fetched assignments:', assignmentsArray);
        setAssignments(Array.isArray(assignmentsArray) ? assignmentsArray : []);
      } catch (assignmentError) {
        if (signal?.aborted) return;
        console.error('CourseView - Error fetching assignments:', assignmentError);
        setAssignments([]);
      }

      if (!signal?.aborted) {
        await detectSyllabusDoc(courseId);
      }

    } catch (error) {
      console.error("Error fetching course data:", error);
      setError("Failed to load course data");
    } finally {
      setLoading(false);
    }
  }, [courseId, detectSyllabusDoc, getEndeavor, getEndeavorEvents, getEndeavorAssignments]);

  // Reset state immediately when courseId changes to prevent stale data
  useEffect(() => {
    setEvents([]);
    setAssignments([]);
    setDocsRefreshKey(k => k + 1);
    setSyllabusDocId(null);
  }, [courseId]);

  useEffect(() => {
    if (!courseId) return;
    
    const abortController = new AbortController();
    fetchCourseData(abortController.signal);
    
    return () => {
      abortController.abort();
    };
  }, [courseId, fetchCourseData]);

  // Load progress overlay for shared courses.
  // Privacy boundary (Phase v1/v2.5): for student_shared courses, peers must
  // not be able to see other peers' progress. Only managers (owner /
  // course_admin / org_admin) load the overlay; for org_owned courses any
  // viewer with admin rights does (loadProgressForEndeavor itself only
  // surfaces aggregate data).
  useEffect(() => {
    if (!isShared || !courseId) return;
    const ownership = course?.ownershipType;
    if (ownership === 'student_shared' && !isManager) return; // suppressed
    loadProgressForEndeavor(Number(courseId));
  }, [isShared, courseId, course?.ownershipType, isManager, loadProgressForEndeavor]);

  // No JS height calc needed; we rely on new CSS flex + overflow model like dashboard.

  // Ensure we start at top BEFORE other components (like chat) might autofocus
  useLayoutEffect(() => {
    // Immediate scroll reset on route/course change
    window.scrollTo(0, 0);
  }, [courseId]);

  // After data loads, force focus to the top sentinel (prevents chat autoFocus scrolling)
  useEffect(() => {
    if (course && topRef.current) {
      topRef.current.focus({ preventScroll: true });
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 50);
    }
  }, [course]);

  // fallback scroll enforcement once course data ready
  useEffect(() => {
    if (!course) return;
    let frames = 0;
    function enforceTop() {
      window.scrollTo(0, 0);
      if (++frames < 4) requestAnimationFrame(enforceTop);
    }
    requestAnimationFrame(enforceTop);
  }, [course]);


  // ✅ OPTIMIZED: Memoized date formatters to prevent recreation on every render
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'Not set';
    
    try {
      let date;
      if (dateString.includes('T')) {
        date = new Date(dateString);
      } else {
        date = new Date(dateString + 'T00:00:00');
      }
      
      if (isNaN(date.getTime())) return 'Invalid date';
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  }, []);

  // ✅ OPTIMIZED: Memoized computed values
  const courseInfo = useMemo(() => {
    if (!course) return null;
    
    return {
      typeName: course.endeavorTypeName || course.endeavorType?.name || 'Course',
      duration: `${formatDate(course.startDate)} - ${formatDate(course.endDate)}`,
      startDate: formatDate(course.startDate),
      endDate: formatDate(course.endDate),
      createdAt: formatDate(course.createdAt),
      updatedAt: formatDate(course.updatedAt)
    };
  }, [course, formatDate]);

  // Early returns for error states only - keep components mounted during loading
  if (error) return <ErrorState error={error} onGoBack={() => navigate('/dashboard')} />;
  
  // Show loading state on initial load (no course data yet)
  if (loading && !course) return <LoadingState />;
  
  // Show not found only when load complete and no course
  if (!loading && !course) return <NotFoundState onGoBack={() => navigate('/dashboard')} />;

  const courseIdNum = Number(courseId);

  return (
    <div className="course-view-container animate-fade-in-up">
      <div
        ref={topRef}
        tabIndex={-1}
        style={{ outline: 'none', height: 0, width: 0, position: 'absolute' }}
        aria-hidden="true"
      />
      <h1 className="page-title" style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
        {course.name}
        {course.ownershipType && (
          <span
            className="course-ownership-badge"
            data-ownership={course.ownershipType}
            style={{
              marginLeft: 10,
              padding: '2px 8px',
              fontSize: '0.7rem',
              fontWeight: 500,
              borderRadius: 999,
              background: course.ownershipType === 'org_owned' ? '#bee3f8'
                : course.ownershipType === 'student_shared' ? '#fefcbf'
                : '#e2e8f0',
              color: '#2d3748',
              verticalAlign: 'middle',
            }}
            title={course.ownershipType === 'org_owned'
              ? 'Owned by an organization'
              : course.ownershipType === 'student_shared'
              ? 'Personal course shared with peers'
              : 'Personal course'}
          >
            {course.ownershipType === 'org_owned' ? 'Org'
              : course.ownershipType === 'student_shared' ? 'Shared'
              : 'Personal'}
          </span>
        )}
        {(isManager || (course.ownershipType === 'org_owned' && hasAnyOrgAdminRole && hasAnyOrgAdminRole())) && (
          <button
            type="button"
            onClick={() => navigate(`/course/${courseId}/admin`)}
            title="Open course admin dashboard (roster, progress, resources)"
            style={{
              marginLeft: 12,
              padding: '4px 12px',
              fontSize: '0.75rem',
              fontWeight: 500,
              borderRadius: 999,
              border: '1px solid var(--border-color)',
              background: 'var(--accent-surface)',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              verticalAlign: 'middle',
            }}
          >
            🛡️ Admin
          </button>
        )}
      </h1>
      <Tabs defaultIndex={0}>
        <Tabs.List data-tour="course-tabs">
          <Tabs.Tab data-tour="course-tab-description">Description</Tabs.Tab>
          <Tabs.Tab data-tour="course-tab-chat">Chat</Tabs.Tab>
          <Tabs.Tab data-tour="course-tab-events">Activities</Tabs.Tab>
          <Tabs.Tab data-tour="course-tab-calendar">Calendar</Tabs.Tab>
          <Tabs.Tab data-tour="course-tab-documents">Documents</Tabs.Tab>
          {isShared && isManager && <Tabs.Tab data-tour="course-tab-manage">Manage</Tabs.Tab>}
        </Tabs.List>
        <Tabs.Panels>
          <Tabs.Panel>
            <CourseDetailsSection
              course={course}
              courseInfo={courseInfo}
              eventsCount={events.length}
              assignmentsCount={assignments.length}
              syllabusDocId={syllabusDocId}
              syllabusDocName={syllabusDocName}
              onEdit={() => navigate(`/course/${courseId}/edit`)}
            />
          </Tabs.Panel>
          <Tabs.Panel>
            <div className="course-section course-chat-section no-header">
              <div className="course-chat-wrapper full-width">
                <AIChatCard
                  mode="endeavor"
                  endeavorId={courseIdNum}
                  endeavorName={course.name}
                  disableAutoFocus={true}
                  documentsRefreshKey={docsRefreshKey}
                  hideHeader
                />
              </div>
            </div>
          </Tabs.Panel>
          <Tabs.Panel>
            <UnifiedActivities courseEndeavorId={courseIdNum} courseName={course.name} />
          </Tabs.Panel>
          <Tabs.Panel>
            {loading ? (
              <div className="course-section">
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading calendar...</p>
                </div>
              </div>
            ) : (
              <CalendarComponent
                mode="course"
                courseEvents={events}
                headerTitle="Course Calendar"
                onEventClick={(event) => {
                  const id = event.eventId || event.id;
                  if (id) navigate(`/event/${id}`);
                }}
                onCreateEvent={(selectedDate) =>
                  navigate(`/create-event?date=${selectedDate.toISOString().split('T')[0]}&course=${courseId}`)
                }
              />
            )}
          </Tabs.Panel>
          <Tabs.Panel>
            <div className="course-section course-documents-section" data-tour="course-documents">
              <h2><span className="section-icon">📄</span> Course Documents</h2>
              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading documents...</p>
                </div>
              ) : (
                <div className="course-docs-row">
                  <div className="course-upload-col">
                    <FileUpload endeavorId={courseIdNum} onUploadComplete={handleUploadComplete} />
                  </div>
                  <div className="course-docs-col">
                    <MyDocumentsCard
                      endeavorId={courseIdNum}
                      refreshKey={docsRefreshKey}
                      maxItems={100}
                    />
                  </div>
                </div>
              )}
            </div>
          </Tabs.Panel>
          {isShared && isManager && (
            <Tabs.Panel>
              <CourseManageTab endeavorId={courseIdNum} />
            </Tabs.Panel>
          )}
        </Tabs.Panels>
      </Tabs>

      <TutorialVideoButton />
    </div>
  );
 }

 export default CourseView;